import React, { useCallback, useEffect, useState } from 'react';
import {
    Button, Card, Col, Dropdown, Empty, Form, Input, InputNumber, Modal, Row, Select, Space, Spin, Table, Tag, Typography, message,
} from 'antd';
import {
    EditOutlined, ExportOutlined, MoreOutlined, PlusOutlined, ReloadOutlined, RocketOutlined, ShoppingCartOutlined, TeamOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import { formatDateTime } from '../../../shared/utils/date';
import PageHeader from '../../../shared/components/PageHeader';
import AddonPanel from '../../../shared/components/AddonPanel';
import diskService, { openDisk } from '../../services/diskService';
import PlanChangeModal from './PlanChangeModal';
import { GB, ModeTag, StatusTag, UsageBar, fmtBytes, listOf, toastErr } from './common';

const { Text, Paragraph } = Typography;

const ROLE_COLOR = { owner: 'gold', admin: 'blue', member: 'default' };
const ROLE_LABEL = { owner: '所有者', admin: '管理员', member: '成员' };

/** 只显示一次的密码 */
const showPassword = (t, title, password) => Modal.success({
    title,
    content: (
        <div>
            <Paragraph copyable={{ text: password }} style={{ marginBottom: 4 }}>
                <Text code style={{ fontSize: 16 }}>{password}</Text>
            </Paragraph>
            <Text type="secondary">{t('Shown only once — copy it now')}</Text>
        </div>
    ),
    okText: t('I have copied it'),
});

const MemberModal = ({ open, member, isSeat, tiers, onCancel, onSubmit }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const editing = !!member;

    useEffect(() => {
        if (!open) return;
        form.resetFields();
        if (member) {
            form.setFieldsValue({
                name: member.name,
                role: member.role === 'owner' ? undefined : member.role,
                seat_tier_gb: member.seat_tier_gb ?? undefined,
                quota_gb: member.quota_bytes ? Math.round((member.quota_bytes / GB) * 100) / 100 : undefined,
            });
        } else {
            form.setFieldsValue({ role: 'member' });
        }
    }, [open, member, form]);

    const tierOptions = Object.entries(tiers || {}).map(([gb, v]) => {
        const free = Math.max(0, (Number(v?.total) || 0) - (Number(v?.used) || 0));
        const mine = member && String(member.seat_tier_gb) === String(gb);
        return { value: Number(gb), label: `${gb} GB · ${t('{{n}} left', { n: free })}`, disabled: free <= 0 && !mine };
    });

    const submit = async () => {
        const v = await form.validateFields();
        const data = { name: v.name, role: v.role };
        if (!editing) data.email = v.email;
        if (isSeat) data.seat_tier_gb = v.seat_tier_gb ?? null;
        else data.quota_gb = v.quota_gb || null;
        setSaving(true);
        try {
            await onSubmit(data);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} title={editing ? t('Edit member') : t('Add member')} onCancel={onCancel} onOk={submit} confirmLoading={saving} destroyOnClose>
            <Form form={form} layout="vertical" preserve={false}>
                {!editing && (
                    <Form.Item name="email" label={t('Email')} rules={[{ required: true, type: 'email', message: t('Enter a valid email address') }]}>
                        <Input placeholder="name@example.com" autoComplete="off" />
                    </Form.Item>
                )}
                <Form.Item name="name" label={t('Full name')} rules={[{ required: true, message: t('Enter a name') }]}>
                    <Input maxLength={120} />
                </Form.Item>
                {member?.role !== 'owner' && (
                    <Form.Item name="role" label={t('Role')} rules={[{ required: true }]}>
                        <Select options={[{ value: 'member', label: t('Members') }, { value: 'admin', label: t('Admin') }]} />
                    </Form.Item>
                )}
                {isSeat ? (
                    <Form.Item name="seat_tier_gb" label={t('Seats')} rules={[{ required: member?.role !== 'owner', message: t('Select a seat') }]}
                        tooltip={tierOptions.length ? undefined : t('Buy seats first')}>
                        <Select options={tierOptions} allowClear={member?.role === 'owner'} placeholder={t('Select seat')} />
                    </Form.Item>
                ) : (
                    <Form.Item name="quota_gb" label={t('Storage cap (GB)')} tooltip={t('Leave empty to share the whole pool')}>
                        <InputNumber min={0.1} step={1} style={{ width: '100%' }} placeholder={t('Unlimited')} />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

const OrgDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, member: null });
    const [planOpen, setPlanOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [o, m] = await Promise.all([diskService.getOrg(id), diskService.listMembers(id)]);
            setInfo(o?.data || null);
            setMembers(listOf(m));
        } catch (e) {
            toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => { load(); }, [load]);

    const org = info?.org || {};
    const usage = info?.usage || {};
    const isSeat = org.quota_mode === 'seat' || org.quota_mode === 'org_seat';
    const isMail = org.kind === 'mail';
    const tiers = usage.seat_tiers && typeof usage.seat_tiers === 'object' && !Array.isArray(usage.seat_tiers) ? usage.seat_tiers : {};
    const subscriptionId = org.subscription_id || info?.subscription_id;
    const ownerAccountId = info?.account_id || org.account_id || org.owner_account_id
        || members.find((m) => m.role === 'owner')?.id;

    const rename = () => {
        let name = org.name;
        Modal.confirm({
            title: t('Rename'),
            icon: <EditOutlined />,
            content: <Input defaultValue={org.name} maxLength={120} onChange={(e) => { name = e.target.value; }} />,
            onOk: async () => {
                if (!name || !name.trim()) { message.warning(t('Please enter a name')); throw new Error('empty'); }
                try {
                    await diskService.renameOrg(id, name.trim());
                    message.success(t('Saved'));
                    load();
                } catch (e) {
                    toastErr(e, t('Failed to save'));
                    throw e;
                }
            },
        });
    };

    const saveMember = async (data) => {
        try {
            if (modal.member) {
                await diskService.updateMember(id, modal.member.id, data);
                message.success(t('Saved'));
            } else {
                const r = await diskService.createMember(id, data);
                const pw = r?.data?.initial_password;
                if (r?.data?.linked_console_user) message.success(t('Added — they sign in with their console account and switch to this org'));
                else if (pw) showPassword(t, t('Member added · initial password'), pw);
                else message.success(t('Member added'));
            }
            setModal({ open: false, member: null });
            load();
        } catch (e) {
            toastErr(e, t('Failed to save'));
        }
    };

    const resetPassword = (m) => Modal.confirm({
        title: t('Reset password for {{name}}?', { name: m.name || m.email }),
        okText: t('Reset'),
        onOk: async () => {
            try {
                const r = await diskService.resetMemberPassword(id, m.id);
                const pw = r?.data?.initial_password;
                if (pw) showPassword(t, t('New password'), pw);
            } catch (e) {
                toastErr(e, t('Operation failed'));
            }
        },
    });

    const toggleStatus = async (m) => {
        try {
            await diskService.updateMember(id, m.id, { status: m.status === 'active' ? 'disabled' : 'active' });
            message.success(m.status === 'active' ? t('Disabled') : t('Enabled'));
            load();
        } catch (e) {
            toastErr(e, t('Operation failed'));
        }
    };

    const remove = (m) => Modal.confirm({
        title: t('Delete member {{name}}?', { name: m.name || m.email }),
        content: t('Their files will be moved to your "Former members" folder'),
        okType: 'danger',
        okText: t('Delete'),
        onOk: async () => {
            try {
                await diskService.deleteMember(id, m.id);
                message.success(t('Deleted'));
                load();
            } catch (e) {
                toastErr(e, t('Deletion failed'));
            }
        },
    });

    const columns = [
        { title: t('Email'), dataIndex: 'email', render: (v) => v || <Text type="secondary">-</Text> },
        { title: t('Full name'), dataIndex: 'name' },
        { title: t('Role'), dataIndex: 'role', width: 90, render: (v) => <Tag color={ROLE_COLOR[v]} style={{ margin: 0 }}>{t(ROLE_LABEL[v] || v)}</Tag> },
        {
            title: isSeat ? t('Seats') : t('Storage cap'), key: 'q', width: 110,
            render: (_, m) => (isSeat
                ? (m.seat_tier_gb ? `${m.seat_tier_gb} GB` : '-')
                : (m.quota_bytes ? fmtBytes(m.quota_bytes) : <Text type="secondary">{t('Unlimited')}</Text>)),
        },
        { title: t('Used'), key: 'used', width: 100, render: (_, m) => fmtBytes(m.used_bytes ?? m.storage_used ?? m.used) },
        { title: t('Status'), dataIndex: 'status', width: 90, render: (v) => <StatusTag status={v} t={t} /> },
        { title: t('Last login'), dataIndex: 'last_login_at', width: 160, render: (v) => (v ? formatDateTime(v) : <Text type="secondary">-</Text>) },
        {
            title: '', key: 'a', width: 90, align: 'right',
            render: (_, m) => (
                <Space size={0}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setModal({ open: true, member: m })} />
                    {m.role !== 'owner' && (
                        <Dropdown trigger={['click']} menu={{
                            items: [
                                // mailbox members use the mail password, linked console users their console one
                                ...((m.auth_source || 'local') === 'local' && !m.linked_console_user
                                    ? [{ key: 'pw', label: t('Reset password'), onClick: () => resetPassword(m) }] : []),
                                { key: 'st', label: m.status === 'active' ? t('Disable') : t('Enable'), onClick: () => toggleStatus(m) },
                                // mail org: members mirror the domain's mailboxes → no manual delete
                                ...(isMail ? [] : [{ type: 'divider' }, { key: 'del', label: t('Delete'), danger: true, onClick: () => remove(m) }]),
                            ],
                        }}>
                            <Button type="link" size="small" icon={<MoreOutlined />} />
                        </Dropdown>
                    )}
                </Space>
            ),
        },
    ];

    const scrollToAddons = () => document.getElementById('disk-org-addons')?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    return (
        <div>
            <PageHeader
                title={org.name || t('Organization')}
                icon={<TeamOutlined />}
                onBack={() => navigate(userPath('/disk'))}
                tags={<Space size={4}>
                    {isMail && <Tag color="purple" style={{ margin: 0 }}>{t('Mail org')}</Tag>}
                    {org.quota_mode && !isMail && <ModeTag mode={org.quota_mode} t={t} />}
                    {org.status && <StatusTag status={org.status} t={t} />}
                    <Button type="text" size="small" icon={<EditOutlined />} onClick={rename} disabled={!org.id && !info} />
                </Space>}
                extra={<Space>
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                    <Button type="primary" icon={<ExportOutlined />} disabled={!ownerAccountId || org.status !== 'active'}
                        onClick={() => openDisk(ownerAccountId, (e) => toastErr(e, t('Redirect failed')))}>{t('Open Drive')}</Button>
                </Space>}
            />

            <Spin spinning={loading && !info}>
                <Row gutter={16}>
                    <Col xs={24} lg={isSeat ? 12 : 24}>
                        <Card size="small" title={t('Usage')} style={{ marginBottom: 16 }}
                            extra={subscriptionId && <Button size="small" icon={<RocketOutlined />} onClick={() => setPlanOpen(true)}>{t('Change plan')}</Button>}>
                            <UsageBar label={t('Storage')} used={Number(usage.used) || 0} limit={Number(usage.limit) || 0} t={t} />
                            {!isSeat && !isMail && (
                                <UsageBar label={t('Members')} used={Number(usage.seats_used) || 0} limit={Number(usage.seat_limit) || 0} fmt={(n) => String(n)} t={t} />
                            )}
                        </Card>
                    </Col>
                    {isSeat && (
                        <Col xs={24} lg={12}>
                            <Card size="small" title={t('Seats')} style={{ marginBottom: 16 }}
                                extra={subscriptionId && <Button size="small" type="primary" icon={<ShoppingCartOutlined />} onClick={scrollToAddons}>{t('Buy seats')}</Button>}>
                                {Object.keys(tiers).length === 0
                                    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No seats yet')} />
                                    : Object.entries(tiers).map(([gb, v]) => (
                                        <UsageBar key={gb} label={`${gb} GB`} used={Number(v?.used) || 0} limit={Number(v?.total) || 0}
                                            fmt={(n) => String(n)} t={t} />
                                    ))}
                            </Card>
                        </Col>
                    )}
                </Row>

                {subscriptionId && <div id="disk-org-addons"><AddonPanel subscriptionId={subscriptionId} onChanged={load} /></div>}

                <Card size="small" title={t('Members')}
                    extra={isMail
                        ? <Text type="secondary">{t('Members are this domain\'s mailboxes — they join by signing in with their mail password')}</Text>
                        : <Button type="primary" size="small" icon={<PlusOutlined />} disabled={org.status && org.status !== 'active'}
                            onClick={() => setModal({ open: true, member: null })}>{t('Add member')}</Button>}>
                    <Table rowKey="id" size="middle" columns={columns} dataSource={members} loading={loading && !!info}
                        pagination={members.length > 20 ? { pageSize: 20 } : false} scroll={{ x: 900 }} />
                </Card>
            </Spin>

            <MemberModal open={modal.open} member={modal.member} isSeat={isSeat} tiers={tiers}
                onCancel={() => setModal({ open: false, member: null })} onSubmit={saveMember} />
            <PlanChangeModal open={planOpen} subscriptionId={subscriptionId} onClose={() => setPlanOpen(false)} />
        </div>
    );
};

export default OrgDetail;
